import React, {Fragment, useCallback, useLayoutEffect, useRef} from 'react';
import {makeStyles} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {Checkbox, Input} from 'components-lib';
import {ListboxComponent} from './ListboxComponent';
import {markAutocompleteAdornmentButtons} from './markAutocompleteAdornmentButtons';
import {colors} from 'styles';
import {IOption} from 'models';
import {isFunction, updateSelectLabel} from 'utils';

interface ISelectAutocompleteProps {
  loading?: boolean;
  multiple: boolean;
  disabled?: boolean;
  options: IOption[];
  values: IOption[];
  placeholder?: string;
  noOptionsText?: string;
  withCheckboxRenderOption?: boolean;
  withFontWeightBold?: boolean;
  disableCloseOnSelect?: boolean;
  /**
   * When true (multi only), the same option can appear more than once in the value
   * (e.g. two "Female" chips). Dropdown clicks always add; remove via chip delete.
   */
  allowDuplicateSelections?: boolean;
  /** Formik / MUI TextField error state */
  error?: boolean;
  helperText?: string | false | string[];
  groupBy?: (option: IOption) => string;
  filterOptions?: (options: IOption[]) => IOption[];
  changeHandler: (option: IOption | IOption[] | null, reason?: string) => void;
  inputChangeHandler?: (value: string) => void;
  blurHandler?: () => void;
}

type ISelectableOption = IOption & {selectionIndex?: number};

export function SelectAutocomplete({
  multiple,
  options,
  changeHandler,
  inputChangeHandler,
  values,
  placeholder,
  disabled = false,
  loading = false,
  blurHandler,
  noOptionsText,
  withCheckboxRenderOption = true,
  withFontWeightBold = false,
  disableCloseOnSelect = false,
  allowDuplicateSelections = false,
  error = false,
  groupBy,
  filterOptions,
}: ISelectAutocompleteProps) {
  const classes = useStyles({withFontWeightBold});
  const rootRef = useRef<HTMLDivElement>(null);
  // Virtualized listbox breaks MUI group headers; use the default list when grouped.
  const listboxComponent = groupBy
    ? undefined
    : (ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>);

  useLayoutEffect(() => {
    markAutocompleteAdornmentButtons(rootRef.current);
  });

  const handleSelectionChange = useCallback(
    (value: IOption | IOption[] | null, reason: string) => {
      // MUI can emit "reset" when options change; never treat that as a user edit.
      if (reason === 'reset' || reason === 'blur') {
        return;
      }
      changeHandler(value, reason);
    },
    [changeHandler]
  );

  const getSingleSelectOption = useCallback((label: string) => {
    const {updatedLabel} = updateSelectLabel(label);

    return (
      <div style={{lineHeight: 1}} title={label}>
        {updatedLabel}
      </div>
    );
  }, []);

  const getMultiSelectOption = useCallback(
    (label: string, selected: boolean) => {
      const {updatedLabel} = updateSelectLabel(label);

      return (
        <Fragment>
          {withCheckboxRenderOption && <Checkbox checked={selected} />}
          {updatedLabel}
        </Fragment>
      );
    },
    [withCheckboxRenderOption]
  );

  const renderInput = useCallback(
    (params) => (
      <Input
        {...params}
        placeholder={placeholder}
        withMargin={false}
        loading={loading}
        disabled={disabled}
        error={error}
        // Form select-autocomplete owns the single Text.Error; never emit MUI helperText.
        helperText={undefined}
      />
    ),
    [placeholder, loading, disabled, error]
  );

  const autocompleteClasses = {
    root: classes.root,
    listbox: classes.listbox,
    tag: classes.tag,
    inputRoot: classes.inputRoot,
  };

  if (!multiple) {
    return (
      <div ref={rootRef} className={classes.wrapper}>
        <Autocomplete<IOption, false, undefined, false>
          classes={autocompleteClasses}
          loading={loading}
          multiple={false}
          disabled={disabled}
          disableCloseOnSelect={disableCloseOnSelect}
          ListboxComponent={listboxComponent}
          options={options}
          groupBy={groupBy}
          filterOptions={filterOptions}
          noOptionsText={noOptionsText}
          getOptionLabel={(option) => option.label}
          getOptionSelected={(option, value) => option.value === value.value}
          onChange={(_event, value, reason) => handleSelectionChange(value, reason)}
          onBlur={blurHandler}
          onInputChange={(_event: any, value: string, reason: string) => {
            if (reason === 'input') {
              isFunction(inputChangeHandler) && inputChangeHandler(value);
            }
          }}
          renderInput={renderInput}
          renderOption={(option: IOption) => getSingleSelectOption(option.label)}
        />
      </div>
    );
  }

  return (
    <div ref={rootRef} className={classes.wrapper}>
      <Autocomplete<ISelectableOption, true, undefined, false>
        classes={autocompleteClasses}
        loading={loading}
        multiple={multiple}
        disabled={disabled}
        value={values as ISelectableOption[]}
        disableListWrap
        disableCloseOnSelect={true}
        ListboxComponent={listboxComponent}
        options={options}
        groupBy={groupBy}
        noOptionsText={noOptionsText}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, value) => {
          if (allowDuplicateSelections) {
            // Only match chip instances against each other so a dropdown click always adds.
            return (
              option.selectionIndex !== undefined &&
              value.selectionIndex !== undefined &&
              option.selectionIndex === value.selectionIndex
            );
          }
          return option.value === value.value || String(option.value) === String(value.value);
        }}
        onChange={(_event, value, reason) => handleSelectionChange(value, reason)}
        onBlur={blurHandler}
        onInputChange={(_event: any, value: string, reason: string) => {
          if (reason === 'input') {
            isFunction(inputChangeHandler) && inputChangeHandler(value);
          }
        }}
        renderInput={renderInput}
        renderOption={(option: ISelectableOption, {selected}) =>
          getMultiSelectOption(
            option.label,
            // With duplicates, checkbox reflects "at least one selected" (clicks still add).
            allowDuplicateSelections ? values.some((v) => v.value === option.value) : selected
          )
        }
      />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  root: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
  tag: {
    margin: theme.spacing(1),
    maxWidth: '100%',

    '& .MuiChip-label': {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    '&:after': {
      content: '""',
      right: 10,
      top: 6,
      height: 12,
      width: 12,
      position: 'absolute',
      backgroundColor: theme.palette.primary.main,
      zIndex: -1,
    },
  },
  inputRoot: ({withFontWeightBold}: {withFontWeightBold: boolean}) => {
    const muiAutocompleteInputFirstChildStyles: any = withFontWeightBold && {
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.grey[900],
    };

    return {
      maxWidth: '100%',
      flexWrap: 'wrap',
      '& .Mui-disabled': {
        color: theme.palette.grey[700],
        backgroundColor: colors.gray,
      },
      '&&[class*="MuiOutlinedInput-root"]': {
        padding: 3.3,
        '& .MuiAutocomplete-input:first-child': muiAutocompleteInputFirstChildStyles,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey[200],
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey[900],
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.main,
      },
    };
  },
}));
